import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "@crema/utility/RichTextEditor";
import { AppState } from "redux/store";
import {
  getInvestorDeskSections,
  updateInvestorDeskSection,
} from "redux/actions";
import { InvestorDeskReport } from "types/models/investorDeskMaster/InvestorDeskMasterApp";

const GlobalIndustryPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { sections } = useSelector<AppState, AppState["investorDeskMaster"]>(
    ({ investorDeskMaster }) => investorDeskMaster,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [title, setTitle] = useState("Global Industry");
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setIsLoading(true);
    dispatch(getInvestorDeskSections());
  }, [dispatch]);

  useEffect(() => {
    if (sections && sections.length > 0) {
      setIsLoading(false);
      const matched = sections.find((item: InvestorDeskReport) => {
        const t = (item?.title || "").toLowerCase();
        return t.includes("global industry") || item.id === 6;
      });

      if (matched) {
        setSectionId(matched.id);
        setTitle(matched.title || "Global Industry");
        setHeading(matched.heading || "");
        setContent(matched.content || "");
      }
    }
  }, [sections]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim() && !content.trim()) {
      setSnackbar({
        open: true,
        message: "Please fill in heading or content",
        severity: "error",
      });
      return;
    }

    if (!sectionId) {
      setSnackbar({
        open: true,
        message: "Global Industry section not found to update",
        severity: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(
        updateInvestorDeskSection(sectionId, {
          title: title || "Global Industry",
          heading,
          content,
          status: "active",
        }) as any,
      );
      setSnackbar({
        open: true,
        message: "Global Industry updated successfully",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to update Global Industry",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 50,
              height: 50,
              borderRadius: "12px",
              color: "primary.main",
            }}
          >
            <PublicOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.4rem", md: "1.75rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Edit Global Industry
            </Typography>
            <Typography variant="body2">
             Update India's standing in global telecommunication industry information
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Main Card */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "#fafafa",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 3,
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                      Global Industry Content
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Heading and content for global industry section
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}
                  >
                    {/* Heading Field */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                          color: "#374151",
                        }}
                      >
                        Heading <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#fff",
                          "& .quill": { height: "auto" },
                          "& .ql-container": {
                            minHeight: "90px",
                            fontSize: 14,
                          },
                        }}
                      >
                        <RichTextEditor
                          value={heading}
                          onChange={(val) => setHeading(val)}
                        />
                      </Box>
                    </Box>

                    {/* Content Field */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                          color: "#374151",
                        }}
                      >
                        Content
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#fff",
                          "& .quill": { height: "auto" },
                          "& .ql-container": {
                            minHeight: "180px",
                            fontSize: 14,
                          },
                        }}
                      >
                        <RichTextEditor
                          value={content}
                          onChange={(val) => setContent(val)}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 4,
                      pt: 3,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isSaving}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3.5,
                        fontWeight: 600,
                      }}
                    >
                      {isSaving ? "Updating..." : "Update"}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GlobalIndustryPage;
